// eslint-disable-next-line max-classes-per-file
import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import UncontrolledTooltip from "reactstrap/lib/UncontrolledTooltip";

import {
  gatherConditionalProps,
  isFunction,
  gatherClasses,
  isSafariBrowser
} from "../../lib/utils";
import "./tooltip.scss";

class Tooltip extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      target: null
    };
    this.ref = React.createRef();
  }

  componentDidMount() {
    this.bindTarget();
  }

  addSafariStyle = target => {
    const { showToolTip, overflowOnly } = this.props;

    if (showToolTip && overflowOnly && isSafariBrowser()) {
      target.classList.add("oui-disable-safari-tooltip");
    }
  };

  bindTarget() {
    // eslint-disable-next-line react/no-find-dom-node
    const target = ReactDOM.findDOMNode(this.ref.current);
    if (target instanceof HTMLElement || target instanceof SVGElement) {
      this.setState({
        target
      });
      this.addSafariStyle(target);
    } else {
      // eslint-disable-next-line no-console
      console.error("Could not find target for the tooltip component.");
    }
  }

  showTooltip = () => {
    const { showToolTip, overflowOnly } = this.props;
    const { target } = this.state;

    if (showToolTip && target) {
      if (overflowOnly) {
        const isOverflow = target.scrollWidth > target.clientWidth;
        return isOverflow;
      }
      return true;
    }
    return false;
  };

  getMaxWidth = () => {
    const { maxWidth } = this.props;
    return String(maxWidth).endsWith("px") ? maxWidth : `${maxWidth}px`;
  };

  render() {
    const { text, dataTestId, className, textAlign, children } = this.props;
    const { target } = this.state;
    const isCustom = isFunction(text);

    return (
      // eslint-disable-next-line react/jsx-fragments
      <React.Fragment>
        {this.showTooltip() ? (
          <UncontrolledTooltip
            data-testid={dataTestId}
            trigger="hover focus"
            boundariesElement="window"
            innerClassName={className}
            className={gatherClasses(
              this.props,
              isCustom ? "oui-custom-tooltip" : null
            )}
            style={
              isCustom
                ? {}
                : {
                    textAlign,
                    maxWidth: this.getMaxWidth()
                  }
            }
            {...gatherConditionalProps(this.props, "placement", "delay")}
            target={target}
          >
            {isCustom ? text() : text}
          </UncontrolledTooltip>
        ) : null}
        <TooltipWrapper ref={this.ref}>{children}</TooltipWrapper>
      </React.Fragment>
    );
  }
}

Tooltip.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  placement: PropTypes.oneOf(["auto", "top", "right", "bottom", "left"]),
  className: PropTypes.string,
  showToolTip: PropTypes.bool,
  overflowOnly: PropTypes.bool,
  textAlign: PropTypes.oneOf(["left", "center", "right"]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataTestId: PropTypes.string,
  delay: PropTypes.number,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func
  ])
};

Tooltip.defaultProps = {
  delay: 300,
  placement: "top",
  showToolTip: true,
  overflowOnly: false,
  textAlign: "left",
  maxWidth: 200,
  className: "",
  dataTestId: "orion-tooltip"
};

Tooltip.displayName = "OrionTooltip";

export default Tooltip;

/**
 * TooltipWrapper will help binding ref on the Tooltip children component
 * As that ref is further used to pass the target element to UncontrolledTooltip
 */
class TooltipWrapper extends React.Component {
  render() {
    const { children } = this.props;
    return children;
  }
}

TooltipWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func
  ])
};
